// AgentScorecardPanel — the Desk ranking rail (owner rulings 2026-09-29).
//
// DEFAULT MODEL = v3, THE SCORE IN LARI (lib/agentValue.js): value created for the agency minus
// resources spent, every rate measured from the whole agency over the last 90 days — money (own
// commission + meetings + clients at their measured value), owner work (capped) with opened
// cooperations weighted above it, every paid repost costs 0.1 ₾ (automated since 2026-11, so it is
// what the agency spent on the agent), and silent scheduled days cost the office's value
// of a working day. Silent days are MANAGEMENT-ONLY: agents see their total, never that part.
// desk_scorecard.model = 'v2' brings back the points model below, 'v1' the old board (IntakeTab).
//
// What changed from AgentRankingPanel (IntakeTab, kept as the `desk_scorecard.model: 'v1'`
// fallback): the score pays for OWNER WORK (cooperation terms, restrictions answers, terms,
// property details, valuation), for RESULTS (followed-up clients, meetings with an outcome,
// deals + commission), is multiplied by ATTENDANCE consistency, and a paid myhome/ss repost with
// no owner work behind it COSTS points. Reposts themselves earn nothing. The rules live in
// lib/rankingScore.js (scoreV2 / relativeFloor / floorStatus); every number is in config.
//
// A fourth tab, "floor", is for management only: agents far behind the OFFICE (no fixed numbers —
// owner, 2026-09-29: "comparing all to all"), which part is weak, and whether the grace period has
// run out. It is never shown to agents as a list of
// colleagues — standing rule: no negative attendance signal on a public board.
import { useEffect, useMemo, useState } from 'react'
import { t, tLog } from '../../lib/i18n.js'
import { tbilisiDayWindow, tbilisiToday, fmtTbilisi } from '../../lib/tbilisiTime.js'
import { Icon, IconText } from '../Icon.jsx'
import { COOP_FILL, COOP_EDGE } from '../../lib/coopMark.js'
import { useAgencyValues } from './useAgencyValues.js'
import { getActivityRanking, getAgentDeals, readDeskCache, writeDeskCache } from '../../db/core.js'
import { getAttendance, getScheduleHistory } from '../../db/index.js'
import { computeAgentStats } from '../../lib/statsCompute.js'
import { buildLedger } from '../../lib/statsModel.js'
import { mergeDealSources } from '../../lib/performance.js'
import { scoreV2, attendanceFromStats, relativeFloor, floorStatus, FLOOR_DEFAULTS, SCORE_V2_DEFAULTS, isManagerRole, boardLines } from '../../lib/rankingScore.js'
import { agentInputs, valueWindow, deriveValues, inReference, VALUE_DEFAULTS } from '../../lib/agentValue.js'

const DAY = 86400000
const PERIOD_DAYS = { daily: 1, weekly: 7, monthly: 30 }
const RANK_TTL_MS = 5 * 60 * 1000       // same reasoning as AgentRankingPanel's TTL note
const FLOOR_TTL_MS = 30 * 60 * 1000

// isManagerRole and boardLines live in lib/rankingScore.js (the Stats tab uses them too);
// re-exported here for the render check.
export { isManagerRole, boardLines }

function windows(period) {
  const now = Date.now()
  if (period === 'daily') {
    // the TBILISI day, not the browser's midnight (audit 2026-10-21: a laptop on another zone shifted "today")
    const mid = new Date(tbilisiDayWindow(tbilisiToday()).startIso)
    return { since: mid.toISOString(), until: null, prevSince: new Date(mid.getTime() - DAY).toISOString(), prevUntil: mid.toISOString() }
  }
  const d = PERIOD_DAYS[period]
  return { since: new Date(now - d * DAY).toISOString(), until: null,
    prevSince: new Date(now - 2 * d * DAY).toISOString(), prevUntil: new Date(now - d * DAY).toISOString() }
}
const isoDay = iso => iso.slice(0, 10)

// Deals per scheduler agent from the ledger: participations + the agent's OWN commission share
// in GEL (buildLedger — the same object the Stats tab reads).
function ledgerByAgent(deals, roster, fx) {
  return buildLedger(mergeDealSources([], deals || [], roster), null, null, fx, roster).byAgent
}

// One window → scored rows for active non-manager roster agents (zero-activity included: an
// empty row is the nudge, documented in AGENT_RANKING_PLAN standing rule 4).
// Attendance for one agent over [since, until), from THE definition (computeAgentStats).
const dayOf = d => { const x = new Date(d); return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}` }
function agentAttendance(agent, attByAgent, schedHist, since, until) {
  const to = until ? dayOf(new Date(until).getTime() - 1) : dayOf(Date.now())
  return attendanceFromStats(computeAgentStats(agent, attByAgent.get(agent.id) || [], 5, dayOf(since), to, schedHist || []))
}

export function scoreWindow({ activity, deals, att, schedHist = [], roster, since, until, points, fx }) {
  const attByAgent = new Map()
  for (const r of att || []) { if (!r?.agent_id) continue; if (!attByAgent.has(r.agent_id)) attByAgent.set(r.agent_id, []); attByAgent.get(r.agent_id).push(r) }
  const end = until ? new Date(until) : new Date()
  const windowDays = Math.max(1, (end - new Date(since)) / DAY)
  const bySched = new Map((activity || []).filter(r => r.scheduler_agent_id).map(r => [r.scheduler_agent_id, r]))
  const led = ledgerByAgent(deals, roster, fx)
  const rows = []
  for (const a of roster) {
    if (a.is_active === false || isManagerRole(a.role)) continue
    // Days this agent was actually on the board inside the window: the owner-work cap and the
    // floor's comparison both scale by it, so a new agent is not measured against a full window.
    const hire = a.hire_date ? new Date(a.hire_date + 'T00:00:00Z') : null
    const effDays = hire && hire > new Date(since) ? Math.max(0, (end - hire) / DAY) : windowDays
    if (effDays <= 0) continue
    const act = bySched.get(a.id) || {}
    const m = led.get(a.id)
    const counts = { ...act, closes: m?.participations || 0, commission: m?.commission || 0 }
    const attendance = agentAttendance(a, attByAgent, schedHist, since, until)
    const s = scoreV2(counts, points, { attendance, capScale: Math.max(1, effDays) / 7 })
    const owner = (Number(act.coop_rich) || 0) + (Number(act.restriction_listings) || 0) + (Number(act.terms_listings) || 0) + (Number(act.detail_properties) || 0)
    const active = owner + (Number(act.reshares) || 0) + (Number(act.meetings) || 0) + counts.closes + (Number(act.clients) || 0) > 0
    rows.push({ id: a.id, name: a.name, score: s.score, parts: s.parts, mult: s.mult, unbacked: s.unbacked, active, effDays,
      closes: counts.closes, meetings: Number(act.meetings_done) || 0, clients: Number(act.clients_followed) || 0, owner,
      reshares: Number(act.reshares) || 0, scraped: Number(act.scraped) || 0 })
  }
  rows.sort((x, y) => y.score - x.score || (x.name || '').localeCompare(y.name || ''))
  return rows
}

// ── v3: the lari model ────────────────────────────────────────────────────────────────────
// One window → board rows (managers off the board). values: the 90-day agency rates, or null
// (then the window measures its own, falling back to the recorded rates when too thin).
export function scoreWindowV3({ activity, deals, att, schedHist = [], roster, since, until, values = null, cfg = null, fx }) {
  const w = valueWindow(agentInputs({ roster, activity, deals, att, schedHist, since, until, fx }), { values, cfg })
  const rows = w.rows.filter(r => !r.isManager).map(r => {
    const c = r.counts || {}
    const n = k => Number(c[k]) || 0
    return { ...r, score: r.value, mult: 1,
      unbacked: n('paid_reposts'),
      active: r.hasTrace, closes: r.closes, meetings: n('meetings_done'), clients: n('clients_followed'),
      coops: r.coopCount, owner: r.items - n('coop_rich') }
  })
  return { rows, values: w.values, dayValue: w.dayValue }
}

// The agency's rates: useAgencyValues (shared with the Stats tab).

function Divider({ tone, label }) {
  return (
    <div className={`flex items-center gap-2 px-2 py-0.5 ${tone === 'red' ? 'text-red-500' : 'text-emerald-600 dark:text-emerald-400'}`}>
      <span className={`h-px flex-1 ${tone === 'red' ? 'bg-red-500/70' : 'bg-emerald-500/60'}`} />
      <span className="text-[11px] font-semibold whitespace-nowrap">{label}</span>
      <span className={`h-px flex-1 ${tone === 'red' ? 'bg-red-500/70' : 'bg-emerald-500/60'}`} />
    </div>
  )
}

function RankRow({ a, i, rows, prevRank, myAgentId, belowLine, isNew = false, showWhy = false, v3 = false }) {
  const mine = myAgentId && a.id === myAgentId
  const was = prevRank.get(a.id)
  const move = was == null ? null : was - (i + 1)
  const gap = i > 0 ? rows[i - 1].score - a.score : 0
  // The breakdown is MANAGEMENT-ONLY (owner, 2026-09-29: "not telling them directly how they are
  // evaluated") — an agent sees colleagues' totals and badges, never the parts behind them.
  const why = !showWhy ? undefined : v3
    ? tLog('desk_score_why3', { owner: a.parts.owner, coops: a.parts.coops ?? 0, results: a.parts.results, reposts: a.parts.reposts, silent: a.parts.silent ?? 0, late: a.parts.late ?? 0 })
    : tLog('desk_score_why', { owner: a.parts.owner, results: a.parts.results, reposts: a.parts.reposts, mult: a.mult.toFixed(2) })
  return (
    <div title={why} className={`flex items-center gap-2 px-2 py-1.5 rounded-lg ${mine ? 'bg-primary/10 border border-primary/40' : ''}`}>
      {/* the rank is information, not decoration - full muted contrast (audit 2026-10-21: /40 read at ~2.2:1) */}
      <span className="text-sm font-bold text-muted-foreground w-5 shrink-0">{i + 1}</span>
      <span className="min-w-0 flex-1">
        <span className="flex items-baseline gap-2">
          <span className="text-xs font-medium truncate">{a.name || t('desk_ranking_unknown')}</span>
          {/* Too new to be judged (owner asked "why is Zura here" — under the line but not red): say so. */}
          {isNew && <span className="text-[10px] font-semibold text-sky-500 shrink-0">{t('desk_ranking_new')}</span>}
          <span className={`ml-auto text-sm font-bold tabular-nums shrink-0 ${a.score < 0 || belowLine ? 'text-red-500' : ''}`}>{a.score}</span>
        </span>
        {/* PLAIN ACTIVITY COUNTS FOR EVERYONE, NO CALCULATION (owner, 2026-09-29: "we show everything
            as it was, not calculating money in front of everyone" — "we don't show calculations, it's on
            front"). What each count is worth, and which reposts count as waste, stay in management's
            hover breakdown; the front shows only what happened.
            WRAPS, never clips (owner screenshot 2026-10-01: a busy row ran off the rail and cut ↻ to
            "↻22…"). Each badge is icon+number with no space inside, so a line breaks only at " · ".
            v3 (owner 2026-11-03): the Activity tab's signs, so one thing never wears two — opened cooperations
            are its cyan diamond and lead the badges, a closed deal its trophy, a meeting its calendar; the rest
            are the same Icon.jsx glyphs (on-screen house rule: SVG, not emoji). ↻ is gone: reposting is
            automated, so the count says nothing about the agent. */}
        {v3 ? (
          <span className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-muted-foreground tabular-nums" title={t('desk_score_legend3')}>
            {move != null && move !== 0 && <span className={`font-semibold ${move > 0 ? 'text-emerald-500' : 'text-red-400'}`}>{move > 0 ? `↑${move}` : `↓${-move}`}</span>}
            <span className="inline-flex items-center gap-1 rounded px-1 font-semibold bg-cyan-500/15 text-cyan-700 dark:text-cyan-300">
              <span className="inline-block w-[7px] h-[7px] rotate-45 rounded-[1px] border" style={{ backgroundColor: COOP_FILL, borderColor: COOP_EDGE }} aria-hidden="true" />{a.coops || 0}
            </span>
            {a.closes > 0 && <span className="inline-flex items-center gap-0.5 text-emerald-500 font-semibold"><Icon name="trophy" size={12} />{a.closes}</span>}
            {a.meetings > 0 && <span className="inline-flex items-center gap-0.5"><Icon name="calendar" size={12} />{a.meetings}</span>}
            {a.clients > 0 && <span className="inline-flex items-center gap-0.5"><Icon name="user" size={12} />{a.clients}</span>}
            <span className="inline-flex items-center gap-0.5"><Icon name="clipboard" size={12} />{a.owner}</span>
            <span className="inline-flex items-center gap-0.5"><Icon name="download" size={12} />{Number(a.counts?.scraped ?? a.scraped) || 0}</span>
          </span>
        ) : (
          <span className="block text-xs text-muted-foreground">
            {move != null && move !== 0 && <span className={`font-semibold ${move > 0 ? 'text-emerald-500' : 'text-red-400'}`}>{move > 0 ? `↑${move}` : `↓${-move}`} · </span>}
            {a.closes > 0 && <span className="text-emerald-500 font-semibold">🏁{a.closes} · </span>}
            {a.meetings > 0 && <>🤝{a.meetings} · </>}
            {a.clients > 0 && <>👤{a.clients} · </>}
            <>📋{a.owner} · 📥{Number(a.counts?.scraped ?? a.scraped) || 0} · ↻{Number(a.counts?.reshares ?? a.reshares) || 0}</>
          </span>
        )}
        {mine && i > 0 && (
          <span className="text-xs font-semibold text-primary block">{tLog('desk_ranking_to_next', { n: gap + 1, rank: i })}</span>
        )}
      </span>
    </div>
  )
}

export function RankRows({ rows, prevRank, myAgentId, lines = null, showWhy = false, v3 = false }) {
  return (
    // ⚠️ min-h: the rail can be shorter than its contents; the list keeps a visible, scrollable budget instead of
    // shrinking to 0 px with every row still in the DOM (audit 2026-10-21, 19 rows in a 0 px list after a resize)
    <div className="space-y-0.5 overflow-y-auto min-h-[12rem] flex-1 max-h-[70vh] pr-0.5">
      {rows.map((a, i) => {
        const prev = i > 0 ? rows[i - 1].score : Infinity
        const crossMedian = lines && prev >= lines.median && a.score < lines.median
        const crossLine = lines && prev >= lines.line && a.score < lines.line
        const belowLine = lines && a.score < lines.line && (a.effDays || 0) >= lines.minDays
        const isNew = !!lines && (a.effDays || 0) < lines.minDays
        const row = <RankRow a={a} i={i} rows={rows} prevRank={prevRank} myAgentId={myAgentId} belowLine={belowLine} isNew={isNew} showWhy={showWhy} v3={v3} />
        return (
          <div key={a.id}>
            {crossMedian && <Divider tone="green" label={tLog('desk_board_median', { n: lines.median })} />}
            {crossLine && <Divider tone="red" label={tLog('desk_board_line', { n: lines.line })} />}
            {row}
          </div>
        )
      })}
    </div>
  )
}

// Pure: the floor from two 30-day windows — ending now, and ending grace_days ago — each scored
// with the SAME scoreWindow the board uses and compared with its own office median.
// (Exported for scripts/render-agent-scorecard.mjs.)
export function computeFloor(data, roster, floorCfg, points, fx, { v3 = false, values = null } = {}) {
  const C = { ...FLOOR_DEFAULTS, ...(floorCfg || {}) }
  const W = Number(C.window_days) || 30
  const at = ts => new Date(ts).toISOString()
  if (v3) {
    const common = { att: data.att, schedHist: data.sh, roster, values, cfg: points, fx }
    const r0 = scoreWindowV3({ ...common, activity: data.m0, deals: data.d0, since: at(data.now - W * DAY), until: at(data.now) }).rows
    const r1 = scoreWindowV3({ ...common, activity: data.m1, deals: data.d1, since: at(data.then - W * DAY), until: at(data.then) }).rows
    const f0 = relativeFloor(r0, C), f1 = relativeFloor(r1, C)
    const out = r0.map(r => ({ id: r.id, name: r.name, sd: r.sd, ...floorStatus(f0.byId.get(r.id), f1.byId.get(r.id), C, new Date(data.now)), ...f0.byId.get(r.id) }))
    out.sort((x, y) => x.norm - y.norm || (x.name || '').localeCompare(y.name || ''))
    return { rows: out, cfg: C, group: f0.group, v3: true }
  }
  const rows0 = scoreWindow({ activity: data.m0, deals: data.d0, att: data.att, schedHist: data.sh, roster, since: at(data.now - W * DAY), until: at(data.now), points, fx })
  const rows1 = scoreWindow({ activity: data.m1, deals: data.d1, att: data.att, schedHist: data.sh, roster, since: at(data.then - W * DAY), until: at(data.then), points, fx })
  const f0 = relativeFloor(rows0, C), f1 = relativeFloor(rows1, C)
  const out = rows0.map(r => {
    const e0 = f0.byId.get(r.id), e1 = f1.byId.get(r.id)
    return { id: r.id, name: r.name, ...floorStatus(e0, e1, C, new Date(data.now)), ...e0 }
  })
  out.sort((x, y) => x.norm - y.norm || (x.name || '').localeCompare(y.name || ''))
  return { rows: out, cfg: C, group: f0.group }
}

// ── the floor: evaluated now and grace_days ago ───────────────────────────────────────────
function useFloor(enabled, roster, floorCfg, points, fx, { delayMs = 0, v3 = false, values = null } = {}) {
  const [data, setData] = useState(null)
  useEffect(() => {
    if (!enabled) return
    let dead = false, timer = null
    const cached = readDeskCache('floor4')
    if (cached) setData(cached)
    if (cached && Date.now() - (cached.__at || 0) < FLOOR_TTL_MS) return () => { dead = true }
    const C = { ...FLOOR_DEFAULTS, ...(floorCfg || {}) }
    const W = Number(C.window_days) || 30
    const now = Date.now(), then = now - C.grace_days * DAY
    const at = ts => new Date(ts).toISOString()
    const load = async () => {
      // Four reads in two pairs: activity_ranking is the Desk's heaviest read.
      const [m0, d0] = await Promise.all([getActivityRanking(at(now - W * DAY)), getAgentDeals(at(now - W * DAY))])
      const [m1, d1] = await Promise.all([getActivityRanking(at(then - W * DAY), at(then)), getAgentDeals(at(then - W * DAY), at(then))])
      const [att, sh] = await Promise.all([
        getAttendance({ dateFrom: isoDay(at(then - W * DAY)), dateTo: isoDay(at(now)) }).catch(() => []),
        getScheduleHistory().catch(() => [])])
      if (dead) return
      const next = { now, then, m0: m0.ranking || [], d0: d0.deals || [], m1: m1.ranking || [], d1: d1.deals || [], att, sh, __at: Date.now() }
      setData(next); writeDeskCache('floor4', next)
    }
    const run = () => load().catch(e => { console.error('[agent-floor]', e.message); if (!dead && !cached) setData({ failed: true }) })
    // An agent's own-line fetch waits a few seconds so it never competes with the Desk's first load.
    if (delayMs) timer = setTimeout(run, delayMs); else run()
    return () => { dead = true; if (timer) clearTimeout(timer) }
  }, [enabled])

  // A failed load must NOT be scored: empty windows would put every agent below the floor.
  return useMemo(() => !data ? null : data.failed ? { failed: true } : computeFloor(data, roster, floorCfg, points, fx, { v3, values }), [data, roster, floorCfg, points, fx, v3, values])
}

const weakText = w => w.map(k => t(`desk_floor_weak_${k}`)).join(' · ')

export function FloorList({ floor }) {
  if (!floor) return <p className="text-xs text-muted-foreground">…</p>
  if (floor.failed) return <p className="text-xs text-red-400">{t('desk_floor_error')}</p>
  const { cfg, group } = floor
  const rows = floor.rows.filter(r => r.status !== 'ok')
  return (
    <div className="space-y-1 overflow-y-auto min-h-0 flex-1 max-h-[70vh]">
      <p className="text-xs text-muted-foreground/80 leading-[1.45]">
        {tLog(floor.v3 ? 'desk_floor_note3' : 'desk_floor_note', { days: cfg.grace_days, date: cfg.enforce_from, pct: Math.round(cfg.ratio * 100),
          median: Math.round(group.median), line: Math.round(group.line), n: group.size })}
      </p>
      {!group.canCompare && <p className="text-xs text-muted-foreground">{t('desk_floor_no_group')}</p>}
      {group.canCompare && rows.length === 0 && <p className="text-xs text-emerald-500">{t('desk_floor_none')}</p>}
      {rows.map(r => (
        <div key={r.id} className={`px-2 py-1.5 rounded-lg border ${r.status === 'leave' ? 'border-red-500/50 bg-red-500/5' : 'border-amber-500/40 bg-amber-500/5'}`}>
          <span className="flex items-baseline gap-2">
            <span className="text-xs font-medium truncate">{r.name}</span>
            <span className={`ml-auto text-xs font-semibold shrink-0 ${r.status === 'leave' ? 'text-red-400' : 'text-amber-500'}`}>
              {t(r.status === 'leave' ? 'desk_floor_leave' : 'desk_floor_at_risk')}{r.trial ? ` · ${t('desk_floor_trial')}` : ''}
            </span>
          </span>
          <span className="text-xs text-muted-foreground block">
            {tLog(floor.v3 ? 'desk_floor_vs3' : 'desk_floor_vs', { norm: r.norm, median: r.median })}
            {r.weak.length > 0 && <> · {weakText(r.weak)}</>}
          </span>
          {/* Silent scheduled days — the leak evidence, for management to ask about (a silent day is
              "nothing recorded", not proof of anything). */}
          {floor.v3 && r.sd && (r.sd.silentRemote.length + r.sd.silentOffice.length + r.sd.absent) > 0 && (
            <span className="text-[11px] text-muted-foreground/80 block">
              {r.sd.silentRemote.length > 0 && <>{tLog('desk_floor_silent_remote', { n: r.sd.silentRemote.length, dates: r.sd.silentRemote.map(d => d.slice(5).split('-').reverse().join('.')).join(', ') })} </>}
              {r.sd.silentOffice.length > 0 && <>{tLog('desk_floor_silent_office', { n: r.sd.silentOffice.length, dates: r.sd.silentOffice.map(d => d.slice(5).split('-').reverse().join('.')).join(', ') })} </>}
              {r.sd.absent > 0 && tLog('desk_floor_absent_n', { n: r.sd.absent })}
            </span>
          )}
        </div>
      ))}
    </div>
  )
}

// THE AGENT'S OWN LINE (owner, 2026-09-29: "we need to tell agents — if you stay in there for that
// long, you leave"). Shown to the agent about THEMSELVES only, never a colleague: their score next to
// the office median and the line, which part trails the group, and how long they have.
export function MyFloor({ floor, myAgentId }) {
  if (!floor || floor.failed || !myAgentId || !floor.group?.canCompare) return null
  const me = floor.rows.find(r => r.id === myAgentId)
  if (!me || !me.judged) return null
  const { cfg } = floor
  const tone = me.status === 'ok' ? 'border-emerald-500/40 bg-emerald-500/5'
    : me.status === 'leave' ? 'border-red-500/50 bg-red-500/5' : 'border-amber-500/40 bg-amber-500/5'
  const headTone = me.status === 'ok' ? 'text-emerald-600 dark:text-emerald-400'
    : me.status === 'leave' ? 'text-red-400' : 'text-amber-600 dark:text-amber-400'
  const head = me.status === 'ok' ? t('desk_floor_me_ok')
    : tLog(me.status === 'leave' ? 'desk_floor_me_leave' : 'desk_floor_me_below', { days: cfg.grace_days })
  return (
    <div className={`mb-2 shrink-0 px-2 py-1.5 rounded-lg border ${tone}`}>
      <p className="text-xs font-semibold">{t('desk_floor_me_title')}</p>
      <p className={`text-xs ${headTone}`}>{head}</p>
      <p className="text-xs text-muted-foreground">
        {/* Status, score and the line only — which parts are weak, and how the line is set, stay with
            management (owner: "agent must not know it or they will use tricks"). */}
        {tLog('desk_floor_me_vs2', { norm: me.norm, line: me.line })}
      </p>
      <p className="text-xs text-muted-foreground/80">
        {tLog('desk_floor_me_rule2', { days: cfg.grace_days })}{me.trial ? ` ${tLog('desk_floor_me_trial', { date: cfg.enforce_from })}` : ''}
      </p>
    </div>
  )
}

export function AgentScorecardPanel({ myAgentId, allAgents = [], config = {}, canSeeFloor = false }) {
  // Month is the default view (owner, 2026-09-29): the lines and the floor judge a month, so the board
  // opens on the window that means something.
  const [period, setPeriod] = useState('monthly')
  const [data, setData] = useState(null)
  const points = config.desk_scorecard
  const fx = config.fx_to_gel
  const v3 = points?.model !== 'v2'
  const values = useAgencyValues(v3, allAgents, points, fx)

  // ⚠️ A FAILED READ IS NEVER SCORED (Desk home audit 2026-10-21). The catch used to hand the scorer empty activity,
  // deals and attendance, which produced ordinary-looking zero scores and an order by name for every agent - a failed
  // request presented as a measured month. Attendance and schedule failures were swallowed into [] the same way. Now
  // ANY failed input fails the window: with a cached board that board stays, dated, with a "could not refresh" note;
  // without one the rail says "unavailable" with Retry. Same rule the floor already followed (useFloor below).
  const [status, setStatus] = useState({ failed: false })
  const [reload, setReload] = useState(0)
  useEffect(() => {
    if (period === 'floor') return
    let dead = false
    const w = windows(period)
    const key = `score4:${period}`
    const cached = readDeskCache(key)
    setData(cached || null)
    setStatus({ failed: false })
    if (cached && !reload && Date.now() - (cached.__at || 0) < RANK_TTL_MS) return () => { dead = true }
    Promise.all([
      getActivityRanking(w.since, w.until), getActivityRanking(w.prevSince, w.prevUntil),
      getAgentDeals(w.since, w.until), getAgentDeals(w.prevSince, w.prevUntil),
      getAttendance({ dateFrom: isoDay(w.prevSince), dateTo: isoDay(new Date().toISOString()) }),
      getScheduleHistory(),
    ]).then(([a, pa, d, pd, att, sh]) => {
      if (dead) return
      const next = { a: a.ranking || [], pa: pa.ranking || [], d: d.deals || [], pd: pd.deals || [], att: att || [], sh: sh || [], w, __at: Date.now() }
      setData(next); writeDeskCache(key, next)
    }).catch(e => { console.error('[agent-scorecard]', e.message); if (!dead) setStatus({ failed: true }) })
    return () => { dead = true }
  }, [period, reload])
  // a board left open re-reads when its cache would have expired (the TTL used to be checked only on a period change),
  // and only while the page is visible
  useEffect(() => {
    if (period === 'floor') return
    const id = setInterval(() => { if (document.visibilityState === 'visible') setReload(n => n + 1) }, RANK_TTL_MS)
    return () => clearInterval(id)
  }, [period])

  const { rows, prevRank } = useMemo(() => {
    if (!data || data.failed || period === 'floor') return { rows: null, prevRank: new Map() }
    const w = data.w || windows(period)
    const common = { att: data.att, schedHist: data.sh, roster: allAgents, points, fx }
    const rows = v3
      ? scoreWindowV3({ ...common, activity: data.a, deals: data.d, since: w.since, until: w.until, values, cfg: points }).rows
      : scoreWindow({ ...common, activity: data.a, deals: data.d, since: w.since, until: w.until })
    const prev = v3
      ? scoreWindowV3({ ...common, activity: data.pa, deals: data.pd, since: w.prevSince, until: w.prevUntil, values, cfg: points }).rows
      : scoreWindow({ ...common, activity: data.pa, deals: data.pd, since: w.prevSince, until: w.prevUntil })
    return { rows, prevRank: new Map(prev.filter(r => r.active).map((r, i) => [r.id, i + 1])) }
  }, [data, period, allAgents, points, fx, v3, values])

  // Management loads the floor when it opens the Floor tab; an agent loads it for their OWN line,
  // a few seconds after the Desk has settled (cached 30 min, like the manager's list).
  const selfFloor = !canSeeFloor && !!myAgentId
  const floor = useFloor((canSeeFloor && period === 'floor') || selfFloor, allAgents, config.desk_floor, points, fx, { delayMs: selfFloor ? 4000 : 0, v3, values })

  const tabBtn = p => `flex-1 px-1 py-1 rounded-md text-xs font-medium transition-colors ${period === p ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`
  // a named group of pressed buttons (audit 2026-10-21: role=tablist held plain buttons with no selected state)
  const periodBtn = (p, label) => (
    <button type="button" className={tabBtn(p)} aria-pressed={period === p} onClick={() => setPeriod(p)}>{label}</button>
  )
  // the window the board really measures, in words: Day = today in Tbilisi, Week/Month = the LAST 7 / 30 days
  const w = (data && data.w) || (period !== 'floor' ? windows(period) : null)
  const dm = iso => fmtTbilisi(iso, { day: '2-digit', month: '2-digit' })
  const windowText = !w ? null : period === 'daily' ? t('desk_ranking_window_today') : `${dm(w.since)}–${dm(new Date().toISOString())}`
  const asOf = data && data.__at ? fmtTbilisi(new Date(data.__at).toISOString(), { hour: '2-digit', minute: '2-digit', hour12: false }) : null
  return (
    <div className="desk-card bg-card border border-border rounded-xl p-4 flex flex-col h-full min-h-0">
      <p className="text-xs font-semibold text-muted-foreground mb-1.5 shrink-0"><IconText>{t('desk_ranking_title')}</IconText>
        {/* agency-wide on purpose (owner rulings) - said, so an office filter elsewhere is not read into it */}
        <span className="ml-1.5 font-normal">· {t('desk_scope_company')}</span></p>
      <div className="flex gap-0.5 mb-1 shrink-0 rounded-lg bg-muted/50 p-0.5" role="group" aria-label={t('desk_ranking_period')}>
        {periodBtn('daily', t('desk_ranking_daily'))}
        {periodBtn('weekly', t('desk_ranking_weekly'))}
        {periodBtn('monthly', t('desk_ranking_monthly'))}
        {canSeeFloor && periodBtn('floor', t('desk_floor_tab'))}
      </div>
      {period !== 'floor' && (
        <p className="text-[11px] text-muted-foreground mb-1.5 shrink-0" aria-live="polite">
          {windowText}{asOf ? ` · ${tLog('desk_ranking_as_of', { t: asOf })}` : ''}
          {status.failed && rows !== null && (
            <span className="text-amber-600 dark:text-amber-400"> · {t('desk_ranking_refresh_failed')}{' '}
              <button type="button" onClick={() => setReload(n => n + 1)} className="underline">{t('desk_ranking_retry')}</button></span>
          )}
        </p>
      )}
      {period === 'floor' ? <FloorList floor={floor} /> : (<>
        {selfFloor && <MyFloor floor={floor} myAgentId={myAgentId} />}
        {/* No legend (owner, 2026-09-29: "delete this"): the badges speak for themselves, and how the
            score is built is not explained to agents. */}
        {rows === null && status.failed
          ? <p className="text-xs text-red-500">{t('desk_ranking_unavailable')}{' '}
              <button type="button" onClick={() => setReload(n => n + 1)} className="underline">{t('desk_ranking_retry')}</button></p>
          : rows === null ? <p className="text-xs text-muted-foreground">…</p>
          : rows.length === 0 ? <p className="text-xs text-muted-foreground">{t('desk_ranking_empty')}</p>
          : <RankRows rows={rows} prevRank={prevRank} myAgentId={myAgentId} showWhy={canSeeFloor} v3={v3}
              // The bottom line judges a MONTH (owner, 2026-09-29: "that below line must be made in 1 month
              // timeframe") — a week is too short to call an agent weak, so Day and Week draw no lines.
              lines={period === 'monthly' ? boardLines(rows, config.desk_floor) : null} />}
      </>)}
    </div>
  )
}
